import { useMemo } from "react";
import type { BaseMerchandisingAlertProps as MerchandisingAlertProps } from "@apac/checkout";
import { useBooleanFlag } from "@apac/feature-flags";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { getMilesEarnedPerDollar, images } from "@hsbc/portal";
import { PremierDestinationEarnDialog } from "@hsbc/portal";
import { FeatureFlagNames } from "@hsbc/utilities";
import { Dialog, DialogContent, DialogTrigger, Icon } from "@tenant/ui";
import { useMerchandisingAlert } from "./hooks";
import styles from "./styles.module.scss";

export const MerchandisingAlert: React.FC<MerchandisingAlertProps> = (
  props
) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "MerchandisingAlert");
  const showMerchandising = useBooleanFlag(
    FeatureFlagNames.SHOW_WALLET_CREDIT_MERCHANDISING_BANNER
  );

  const { premierMastercard, earns, currentPromotion } =
    useMerchandisingAlert(props);

  const { milesEarn, pointsEarn } = useMemo(() => {
    return {
      milesEarn: currentPromotion?.awards.multiplier ?? 0,
      pointsEarn: currentPromotion
        ? getMilesEarnedPerDollar(currentPromotion)
        : 0,
    };
  }, [currentPromotion]);

  if (!(showMerchandising ?? false)) {
    return null;
  }

  if (premierMastercard && premierMastercard.hasCard) {
    return (
      <Dialog>
        <DialogTrigger>
          <div className={cn("premier-mastercard")}>
            <img src={images.premierMastercard} alt="HSBC Premier Mastercard" />
            <div className={cn("card-benefits-title")}>
              {premierMastercard.isSelected ? (
                <Trans
                  i18nKey="premierDestinations.checkoutBannerSelected"
                  components={[<span className={cn("underline")} />]}
                  values={{
                    pointsEarn: premierMastercard.pointsEarn,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="premierDestinations.checkoutBanner"
                  components={[<span className={cn("underline")} />]}
                  values={{
                    milesEarn,
                    pointsEarn,
                  }}
                />
              )}
            </div>
          </div>
        </DialogTrigger>
        <DialogContent
          title={t("banners.premierDestination.dialog.title")}
          className={cn("dialog-content")}
        >
          <PremierDestinationEarnDialog />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className={block}>
      {earns.length > 0 && (
        <div className={cn("card-benefits")}>
          <Icon
            className={cn("card-benefits-icon")}
            iconName={IconNameEnum.infoFilled}
            useCurrentColor={false}
          />
          <div className={cn("card-benefits-text-container")}>
            <span className={cn("card-benefits-title")}>{earns[0].title}</span>
            <span className={cn("card-benefits-subtitle")}>
              {earns[0].subtitle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
